// Runtime API/ID endpoint resolution for the customer app (DEV-1057).
//
// A leaf module with NO heavy imports. It owns (a) the pure host-derivation
// helpers and (b) a mutable store of the endpoints resolved at bootstrap. Both
// `config.ts` (which bakes the `API_URL`/`ID_URL` consts) and
// `resolve-endpoints.ts` (the probe) import from here, so the pure helpers can
// be used WITHOUT triggering `config.ts`'s const freeze — that freeze must
// happen only after the store is populated (see `main.tsx` bootstrap order).

// Cloudflare Pages branch-preview apex for the customer app (apps/app). A
// `<slug>.symoditi-go.pages.dev` host is a per-PR preview stand. Mirrors the
// server-side `PREVIEW_ORIGIN_SUFFIXES` / `validatePreviewRedirectUri` check.
export const APP_PREVIEW_HOST_SUFFIX = '.symoditi-go.pages.dev';

/**
 * True when `hostname` is a non-empty subdomain directly under the app's Pages
 * preview apex. The bare apex `symoditi-go.pages.dev` is the production CF alias
 * (not a preview) and is excluded, matching the server's non-empty-label rule.
 */
export function isAppPreviewHost(hostname: string): boolean {
  const host = hostname.toLowerCase();
  if (!host.endsWith(APP_PREVIEW_HOST_SUFFIX)) return false;
  const label = host.slice(0, host.length - APP_PREVIEW_HOST_SUFFIX.length);
  return label.length > 0 && !label.startsWith('.') && !label.endsWith('.');
}

/**
 * The per-PR API stand slug for a head branch (DEV-1617). KEEP IN SYNC with
 * `scripts/preview-stand-names.sh` — the name source `preview-api.yml` uses for
 * the Fly app `symoditi-api-<slug>`, the Neon branch and the ClickHouse
 * database. Both implementations are pinned to the same golden vectors
 * (`scripts/preview-stand-names.vectors.json`).
 *
 * `<normalized head ref, cut to 21 chars>-<first 8 hex of sha256(head ref)>`:
 * the hash is over the RAW ref, so branches sharing a long prefix (or
 * normalizing identically, `feat/x` vs `feat-x`) never share a stand. Only
 * ASCII A-Z is lowercased, matching `tr 'A-Z' 'a-z'` under `LC_ALL=C`.
 *
 * Async because the browser's `crypto.subtle.digest` is.
 */
export async function derivePreviewStandSlug(headRef: string): Promise<string> {
  const normalized = headRef
    .replace(/[A-Z]/g, (c) => c.toLowerCase())
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  const prefix = normalized.slice(0, 21).replace(/-+$/, '');
  const digest = await globalThis.crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(headRef)
  );
  const hash = Array.from(new Uint8Array(digest).slice(0, 4), (b) =>
    b.toString(16).padStart(2, '0')
  ).join('');
  return prefix ? `${prefix}-${hash}` : hash;
}

/**
 * On a Pages preview host, the per-PR API stand of the given head branch:
 * `https://symoditi-api-<slug>.fly.dev`. Returns null off a preview host (the
 * label is guaranteed non-empty by `isAppPreviewHost`).
 *
 * The head ref is a required input since DEV-1617: the stand slug carries a
 * hash of the FULL branch name, which the Pages host label (Cloudflare cuts it
 * to 28 chars) cannot reproduce. Deriving the stand from the host label alone
 * is exactly the truncation that made two PRs share one stand.
 *
 * NOT WIRED INTO ENDPOINT RESOLUTION, and must not be re-wired as-is: the
 * derived host usually does not exist (a frontend-only PR creates no stand) and
 * Fly app names are a single globally-claimable namespace, so electing it by
 * liveness probe would let a squatter serve our login page. `resolveAndStoreEndpoints`
 * resolves preview hosts to prod for exactly that reason. Kept because the
 * follow-up (a stand on a domain we control, serving its own login page) needs
 * this derivation; it will have to supply the head ref (e.g. CF_PAGES_BRANCH at
 * build time).
 */
export async function deriveAppApiStandUrl(
  hostname: string,
  headRef: string
): Promise<string | null> {
  if (!isAppPreviewHost(hostname)) return null;
  return `https://symoditi-api-${await derivePreviewStandSlug(headRef)}.fly.dev`;
}

export interface ResolvedEndpoints {
  apiUrl: string;
  idUrl: string;
}

let resolved: ResolvedEndpoints | null = null;

/** Set by `resolveAndStoreEndpoints` before the app bootstraps. */
export function setResolvedEndpoints(endpoints: ResolvedEndpoints): void {
  resolved = endpoints;
}

/** Read by `config.ts` when baking `API_URL`/`ID_URL`. Null off-preview. */
export function getResolvedEndpoints(): ResolvedEndpoints | null {
  return resolved;
}
